import { motion } from 'framer-motion';

/**
 * SplitText — splits a string into individual <span> letters that animate
 * independently (stagger reveal from below).
 *
 * Usage:
 *   <SplitText text="Built for creators" className="text-6xl font-black" />
 *
 * Props:
 *  - text:       the string (required)
 *  - delay:      initial delay before animation starts (default 0)
 *  - stagger:    seconds between letter reveals (default 0.025)
 *  - duration:   per-letter animation duration (default 0.55)
 *  - once:       run animation only once (default true)
 *  - viewport:   whileInView margin (default '-80px')
 *  - direction:  'up' | 'down' | 'left' | 'right' (default 'up')
 *  - hoverShatter: if true, hovering individual letters jitters them
 */
export default function SplitText({
  text = '',
  delay = 0,
  stagger = 0.025,
  duration = 0.55,
  once = true,
  viewport = '-80px',
  direction = 'up',
  hoverShatter = false,
  className = '',
  as = 'span',
  ...rest
}) {
  const fromTransform = {
    up:    { y: 36, opacity: 0 },
    down:  { y: -36, opacity: 0 },
    left:  { x: -28, opacity: 0 },
    right: { x: 28, opacity: 0 },
  }[direction];
  const toTransform = { x: 0, y: 0, opacity: 1 };

  const words = text.split(' ');
  const Component = motion[as] || motion.span;

  let charIdx = 0;
  return (
    <Component
      className={`inline-block split-text ${className}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, margin: viewport }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
      aria-label={text}
      {...rest}
    >
      {words.map((word, wi) => (
        <span key={wi} className="inline-block whitespace-nowrap mr-[0.25em]">
          {Array.from(word).map((ch) => {
            charIdx++;
            return (
              <motion.span
                key={charIdx}
                aria-hidden
                className={`inline-block split-letter ${hoverShatter ? 'hover:animate-glitch' : ''}`}
                variants={{
                  hidden: fromTransform,
                  visible: { ...toTransform, transition: { duration, ease: [0.16, 1, 0.3, 1] } },
                }}
              >
                {ch}
              </motion.span>
            );
          })}
        </span>
      ))}
    </Component>
  );
}
